import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { BuildingZones, EnvironmentalData } from '../../types/energyData';
import { EnergyDataService } from '../../services/energyDataService';

interface BuildingDetailViewProps {
  data: BuildingZones;
  environmental: EnvironmentalData;
  energyDataService: EnergyDataService;
}

export const BuildingDetailView: React.FC<BuildingDetailViewProps> = ({ data, environmental, energyDataService }) => {
  const { t } = useTranslation();
  
  // Get REAL time series data from JSON files - NO FAKE DATA
  const buildingTimeSeriesData = energyDataService.getBuildingTimeSeriesData();
  
  // Use real data if available, otherwise show current values only
  const hasBuildingData = buildingTimeSeriesData.tz1Data.length > 0;
  
  const timeData = hasBuildingData ? buildingTimeSeriesData.timeLabels : ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
  
  // Use REAL data from JSON files - NO SINE WAVES OR FAKE PATTERNS
  const tz1TempData = hasBuildingData ? buildingTimeSeriesData.tz1Data : [data.tz1.temperature, data.tz1.temperature, data.tz1.temperature, data.tz1.temperature, data.tz1.temperature, data.tz1.temperature, data.tz1.temperature];
  const r1TempData = hasBuildingData ? buildingTimeSeriesData.r1Data : [data.rooms[0]?.temperature || 20.0, data.rooms[0]?.temperature || 20.0, data.rooms[0]?.temperature || 20.0, data.rooms[0]?.temperature || 20.0, data.rooms[0]?.temperature || 20.0, data.rooms[0]?.temperature || 20.0, data.rooms[0]?.temperature || 20.0];
  const r2TempData = hasBuildingData ? buildingTimeSeriesData.r2Data : [data.rooms[1]?.temperature || 20.0, data.rooms[1]?.temperature || 20.0, data.rooms[1]?.temperature || 20.0, data.rooms[1]?.temperature || 20.0, data.rooms[1]?.temperature || 20.0, data.rooms[1]?.temperature || 20.0, data.rooms[1]?.temperature || 20.0];
  const r3TempData = hasBuildingData ? buildingTimeSeriesData.r3Data : [data.rooms[2]?.temperature || 20.0, data.rooms[2]?.temperature || 20.0, data.rooms[2]?.temperature || 20.0, data.rooms[2]?.temperature || 20.0, data.rooms[2]?.temperature || 20.0, data.rooms[2]?.temperature || 20.0, data.rooms[2]?.temperature || 20.0];
  
  // Use REAL ambient temperature data from JSON files
  const ambientTempData = hasBuildingData && buildingTimeSeriesData.ambientData.length > 0 
    ? buildingTimeSeriesData.ambientData 
    : [environmental.ambientTemp, environmental.ambientTemp, environmental.ambientTemp, environmental.ambientTemp, environmental.ambientTemp, environmental.ambientTemp, environmental.ambientTemp];
  
  // Power data should be based on current values only
  const tz1PowerData = [data.tz1.power, data.tz1.power, data.tz1.power, data.tz1.power, data.tz1.power, data.tz1.power, data.tz1.power];

  const temperatureChartOption = {
    title: {
      text: t('building.buildingTemperaturesOverTime'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          result += `${param.seriesName}: ${param.value}°C<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [t('building.thermalZoneTZ1'), t('building.room1'), t('building.room2'), t('building.room3'), t('building.ambient')],
      top: 30
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      min: function(value: any) {
        // Ensure ambient temperature is visible by setting minimum below the lowest temperature
        const allTemps = [...tz1TempData, ...r1TempData, ...r2TempData, ...r3TempData, ...ambientTempData];
        return Math.min(...allTemps) - 2;
      },
      max: function(value: any) {
        // Set maximum above the highest temperature
        const allTemps = [...tz1TempData, ...r1TempData, ...r2TempData, ...r3TempData, ...ambientTempData];
        return Math.max(...allTemps) + 2;
      },
      axisLabel: {
        formatter: '{value}°C'
      }
    },
    series: [
      {
        name: t('building.thermalZoneTZ1'),
        data: tz1TempData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#EF4444', width: 3 }
      },
      {
        name: t('building.room1'),
        data: r1TempData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#3B82F6' }
      },
      {
        name: t('building.room2'),
        data: r2TempData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#10B981' }
      },
      {
        name: t('building.room3'),
        data: r3TempData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#8B5CF6' }
      },
      {
        name: t('building.ambient'),
        data: ambientTempData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#6B7280', type: 'dashed' }
      }
    ]
  };

  const powerChartOption = {
    title: {
      text: t('building.thermalPowerToBuilding'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        const value = params[0].value;
        const type = value < 0 ? 'Heating' : value > 0 ? 'Cooling' : 'Neutral';
        return `${params[0].axisValue}: ${Math.abs(value).toFixed(1)} kW (${type})`;
      }
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} kW'
      }
    },
    series: [{
      data: tz1PowerData,
      type: 'bar',
      itemStyle: {
        color: function(params: any) {
          return params.value < 0 ? '#EF4444' : params.value > 0 ? '#3B82F6' : '#6B7280';
        }
      }
    }]
  };

  const getSeasonModeColor = (mode: string) => {
    switch (mode) {
      case 'Heating': return '#EF4444';
      case 'Cooling': return '#3B82F6';
      case 'Off': return '#6B7280';
      default: return '#10B981';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🏠 {t('building.buildingZoneManagement')}</h2>
            <p className="text-gray-600">{t('building.thermalZoneControl')}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-red-600">{data.tz1.temperature.toFixed(1)}°C</div>
            <div className="text-sm text-gray-500">{t('building.tz1Temperature')}</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={temperatureChartOption} style={{ height: '300px' }} />
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={powerChartOption} style={{ height: '300px' }} />
        </div>
      </div>

      {/* Zone Status Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* TZ1 Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🏠 {t('building.thermalZoneTZ1')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('building.currentTemperature')}</span>
              <span className="font-semibold text-lg">{data.tz1.temperature.toFixed(1)}°C</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('building.seasonMode')}</span>
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: getSeasonModeColor(data.tz1.seasonMode) }}
              >
                {data.tz1.seasonMode}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('building.thermalPower')}</span>
              <span className="font-semibold">{data.tz1.power.toFixed(1)} kW</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Supply Temp Expected</span>
              <span className="font-semibold">{data.tz1.supplyTempExpected.toFixed(1)}°C</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Ambient Temperature</span>
              <span className="font-semibold">{environmental.ambientTemp.toFixed(1)}°C</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Temperature Delta</span>
              <span className="font-semibold">{(data.tz1.temperature - environmental.ambientTemp).toFixed(1)}°C</span>
            </div>
          </div>
        </div>

        {/* Room Temperatures */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🏠 Individual Rooms</h3>
          <div className="space-y-4">
            {data.rooms.map((room, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{room.room}</div>
                  <div className="text-sm text-gray-500">Temperature</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-gray-900">{room.temperature.toFixed(1)}°C</div>
                  <div className="text-xs text-gray-500">{room.unit}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Environmental Conditions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🌤️ Environmental Conditions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600">{environmental.ambientTemp.toFixed(1)}°C</div>
            <div className="text-sm text-gray-600">Outdoor Temperature</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-3xl font-bold text-yellow-600">{environmental.solarRadiation.toFixed(0)}</div>
            <div className="text-sm text-gray-600">Solar Radiation (W/m²)</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-3xl font-bold text-orange-600">{environmental.radiationExpected.toFixed(0)}</div>
            <div className="text-sm text-gray-600">Expected Radiation (W/m²)</div>
          </div>
        </div>
      </div>

      {/* Thermal Performance */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🔥 Thermal Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Zone TZ1 Performance</h4>
            <div className="space-y-2">
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Building Statistics</h4>
            <div className="space-y-2">
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
